import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Alert,
  Table,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import OrderLineItemDetails from '../../components/Orders/OrderLineItemDetails';
import { GET_ORDER, GetOrderData, GetOrderVariables } from '../../graphql/queries/orders';
import { formatOrderTotal } from '../../utils/orderLineItemMeta';
import BookingStatusSelect from '../../components/Bookings/BookingStatusSelect';
import VoucherActions from '../../components/Bookings/VoucherActions';
import CustomerCancelRequestButton from '../../components/Orders/CustomerCancelRequestButton';
import BookingItineraryPanel from '../../components/Operations/BookingItineraryPanel';
import AccessDenied from '../Utility/AccessDenied';
import { useOwnOrderAccess } from '../../hooks/useOwnOrderAccess';

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

const BookingDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  document.title = id ? `Booking #${id} | NGK Tours` : 'Booking | NGK Tours';

  const { data, loading, error } = useQuery<GetOrderData, GetOrderVariables>(GET_ORDER, {
    variables: { id: id || '', idType: 'DATABASE_ID' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  const order = data?.order;
  const { denied } = useOwnOrderAccess(order, { loading: loading && !order, error });

  if (!id) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Booking" breadcrumbItem="Booking Details" />
          <Alert color="warning">Booking ID is missing.</Alert>
          <Link to="/bookings-list">Back to Bookings</Link>
        </Container>
      </div>
    );
  }

  if (denied) {
    return <AccessDenied />;
  }

  if (loading && !order) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Booking" breadcrumbItem="Booking Details" />
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading booking...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Booking" breadcrumbItem="Booking Details" />
          <Alert color="danger">{(error as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors?.[0]?.message || error.message}</Alert>
          <Link to="/bookings-list">Back to Bookings</Link>
        </Container>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Booking" breadcrumbItem="Booking Details" />
          <Alert color="info">Booking not found.</Alert>
          <Link to="/bookings-list">Back to Bookings</Link>
        </Container>
      </div>
    );
  }

  const customerName =
    order.billing?.firstName || order.billing?.lastName
      ? [order.billing.firstName, order.billing.lastName].filter(Boolean).join(' ')
      : order.billing?.email || '—';

  const lineItems = order.lineItems?.nodes ?? [];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title={`Booking #${order.orderNumber ?? order.databaseId}`}
          breadcrumbItem="Booking Details"
        />
        <Row>
          <Col lg={8}>
            <Card>
              <CardBody>
                <h5 className="mb-3">Order #{order.orderNumber ?? order.databaseId}</h5>
                <Row className="mb-2">
                  <Col sm={4} className="text-muted">Status</Col>
                  <Col sm={8}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <BookingStatusSelect
                        orderId={order.databaseId}
                        status={order.status}
                        compact={false}
                      />
                      <CustomerCancelRequestButton
                        orderId={order.databaseId}
                        status={order.status}
                        size="sm"
                      />
                    </div>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={4} className="text-muted">Date</Col>
                  <Col sm={8}>{formatDate(order.date)}</Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={4} className="text-muted">Customer</Col>
                  <Col sm={8}>{customerName} {order.billing?.email && `(${order.billing.email})`}</Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={4} className="text-muted">Payment</Col>
                  <Col sm={8}>{order.paymentMethodTitle || '—'}</Col>
                </Row>

                <hr />
                <h6 className="mb-3">Booking items</h6>
                {lineItems.length === 0 ? (
                  <p className="text-muted mb-0">No line items.</p>
                ) : (
                  lineItems.map((item, index) => (
                    <div
                      key={`${item.productId ?? 'item'}-${index}`}
                      className={`border rounded p-3 ${index < lineItems.length - 1 ? 'mb-3' : ''}`}
                    >
                      <OrderLineItemDetails
                        productName={item.product?.node?.name || 'Product'}
                        meta={item.metaData}
                        quantity={item.quantity}
                        total={item.total ?? undefined}
                        currency={order.currency}
                      />
                    </div>
                  ))
                )}

                <BookingItineraryPanel order={order} />

                <Row className="mt-3">
                  <Col sm={{ size: 6, offset: 6 }}>
                    <Table size="sm" className="mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted">Total</td>
                          <td>
                            <strong>{formatOrderTotal(order.total, order.currency)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col lg={4}>
            <Card>
              <CardBody>
                <h6 className="mb-3">Customer</h6>
                <p className="mb-1">
                  {[order.billing?.firstName, order.billing?.lastName].filter(Boolean).join(' ')}
                </p>
                <p className="mb-1 text-muted small">{order.billing?.email}</p>
                <p className="mb-0 text-muted small">{order.billing?.phone}</p>
                <p className="mt-2 mb-0 text-muted small">
                  {[order.billing?.address1, order.billing?.address2, order.billing?.city, order.billing?.state, order.billing?.postcode, order.billing?.country]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
          <Link to="/bookings-list" className="btn btn-secondary">Back to Bookings</Link>
          <Link to={`/orders/${order.databaseId}`} className="btn btn-primary">View full order</Link>
          <VoucherActions order={order} size="md" />
        </div>
      </Container>
    </div>
  );
};

export default BookingDetails;
